import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ReCAPTCHA from "react-google-recaptcha";
import { getSiteConfig } from "@/config/siteConfigs";
import styles from "./Form.module.scss";

const KariereForm: React.FC = () => {
  const { t } = useTranslation("common");
  const siteConfig = getSiteConfig();

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    birthDate: "",
    salaryExpectation: "",
    startDate: "",
    email: "",
    phone: "",
    jobTitle: "",
    message: "",
    consent: false,
    consentDataProcessing: false,
    services: {
      diagnosis: false,
      partsExchange: false,
      repairs: false,
      maintenance: false,
      others: false,
    },
  });

  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value, type } = e.target;

    if (type === "checkbox" && "checked" in e.target) {
      const { checked } = e.target as HTMLInputElement;
      if (name in formData.services) {
        setFormData((prev) => ({
          ...prev,
          services: { ...prev.services, [name]: checked },
        }));
      } else {
        setFormData((prev) => ({ ...prev, [name]: checked }));
      }
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.consent || !formData.consentDataProcessing) {
      alert(t("careerForm.errors.consent"));
      return;
    }

    if (!captchaToken) {
      alert(t("careerForm.errors.recaptcha"));
      return;
    }

    const formPayload = {
      type: "feedback",
      feedbacktype: "feedbackcareer",
      legid: 1,
      language: siteConfig.language,
      data: {
        firstName: formData.firstName,
        lastName: formData.lastName,
        birthDate: formData.birthDate,
        salaryExpectation: formData.salaryExpectation,
        startDate: formData.startDate,
        email: formData.email,
        phone: formData.phone,
        jobTitle: formData.jobTitle,
        message: formData.message,
        recaptchaToken: captchaToken,
      },
    };

    try {
      setLoading(true);
      const response = await sendToCRM(formPayload);
      console.log("CRM Response:", response);
      alert(t("careerForm.success"));

      if (window.dataLayer) {
        window.dataLayer.push({
          event: "form_submit",
          form_name: "career_form",
          form_type: "feedbackcareer",
        });
      }

      setFormData({
        firstName: "",
        lastName: "",
        birthDate: "",
        salaryExpectation: "",
        startDate: "",
        email: "",
        phone: "",
        jobTitle: "",
        message: "",
        consent: false,
        consentDataProcessing: false,
        services: {
          diagnosis: false,
          partsExchange: false,
          repairs: false,
          maintenance: false,
          others: false,
        },
      });

      recaptchaRef.current?.reset();
      setCaptchaToken(null);
    } catch (error) {
      console.error("Fehler beim Senden der Bewerbung", error);
      alert(t("careerForm.error"));
    } finally {
      setLoading(false);
    }
  };

  const sendToCRM = async (data: any) => {
    const res = await fetch("https://crm.hixa.eu/external/goodsapi/", {
      method: "POST",
      headers: {
        Authorization: "Bearer DH345DFGDGb32FDsf03876SDFSdf13",
        "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
        Accept: "application/json",
      },
      body: new URLSearchParams({
        type: data.type,
        legid: "1",
        language: siteConfig.language,
        feedbacktype: data.feedbacktype,
        data: JSON.stringify(data.data),
      }),
    });

    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(`Failed to send message to CRM: ${res.status} ${text}`);
    }
    return res.json();
  };

  return (
    <div className={`container ${styles.contactSection}`}>
      <div className={`sideP88 sectionContainerWhite ${styles.formContainer}`}>
        <div className={styles.contactSectionBlock}>
          <div className={styles.sectionDesk}>
            <h3 className={styles.heading}>{t("careerForm.heading")}</h3>
            <p className={styles.subheading}>{t("careerForm.subheading")}</p>
          </div>
          <form onSubmit={handleSubmit}>
            <div className={styles.doubleInput}>
              <div className={styles.formGroup}>
                <label htmlFor="firstName" className={styles.label}>
                  {t("careerForm.fields.firstName.label")} *
                </label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  placeholder={`${t(
                    "careerForm.fields.firstName.placeholder"
                  )} *`}
                  value={formData.firstName}
                  onChange={handleChange}
                  className={styles.input}
                  required
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="lastName" className={styles.label}>
                  {t("careerForm.fields.lastName.label")} *
                </label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  placeholder={`${t(
                    "careerForm.fields.lastName.placeholder"
                  )} *`}
                  value={formData.lastName}
                  onChange={handleChange}
                  className={styles.input}
                  required
                />
              </div>
            </div>

            <div className={styles.doubleInput}>
              <div className={`${styles.formGroup} ${styles.w31}`}>
                <label htmlFor="birthDate" className={styles.label}>
                  {t("careerForm.fields.birthDate.label")} *
                </label>
                <input
                  type="date"
                  id="birthDate"
                  name="birthDate"
                  value={formData.birthDate}
                  onChange={handleChange}
                  className={styles.input}
                  required
                />
              </div>

              <div className={`${styles.formGroup} ${styles.w31}`}>
                <label htmlFor="salaryExpectation" className={styles.label}>
                  {t("careerForm.fields.salaryExpectation.label")} *
                </label>
                <input
                  type="text"
                  id="salaryExpectation"
                  name="salaryExpectation"
                  placeholder={t(
                    "careerForm.fields.salaryExpectation.placeholder"
                  )}
                  value={formData.salaryExpectation}
                  onChange={handleChange}
                  className={styles.input}
                  required
                />
              </div>

              <div className={`${styles.formGroup} ${styles.w31}`}>
                <label htmlFor="startDate" className={styles.label}>
                  {t("careerForm.fields.startDate.label")}
                </label>
                <input
                  type="date"
                  id="startDate"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleChange}
                  className={styles.input}
                  required
                  lang="en"
                />
              </div>
            </div>

            <div className={styles.doubleInput}>
              <div className={styles.formGroup}>
                <label htmlFor="email" className={styles.label}>
                  {t("careerForm.fields.email.label")} *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  placeholder={`${t("careerForm.fields.email.placeholder")} *`}
                  value={formData.email}
                  onChange={handleChange}
                  className={styles.input}
                  required
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="phone" className={styles.label}>
                  {t("careerForm.fields.phone.label")} *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  placeholder={`${t("careerForm.fields.phone.placeholder")} *`}
                  value={formData.phone}
                  onChange={handleChange}
                  className={styles.input}
                  required
                />
              </div>
            </div>

            <div className={`${styles.formGroup} ${styles.pb20}`}>
              <label htmlFor="jobTitle" className={styles.label}>
                {t("careerForm.fields.jobTitle.label")} *
              </label>
              <input
                type="text"
                id="jobTitle"
                name="jobTitle"
                placeholder={t("careerForm.fields.jobTitle.placeholder")}
                value={formData.jobTitle}
                onChange={handleChange}
                className={styles.input}
                required
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="message" className={styles.label}>
                {t("careerForm.fields.message.label")} *
              </label>
              <textarea
                id="message"
                name="message"
                placeholder={`${t("careerForm.fields.message.placeholder")} *`}
                value={formData.message}
                onChange={handleChange}
                className={styles.textarea}
                required
              />
            </div>

            <div className={styles.consent}>
              <input
                type="checkbox"
                id="consent"
                name="consent"
                checked={formData.consent}
                onChange={handleChange}
                className={styles.checkbox}
                required
              />
              <label htmlFor="consent">{t("careerForm.consent")}</label>
            </div>

            <div className={styles.consent}>
              <input
                type="checkbox"
                id="consentDataProcessing"
                name="consentDataProcessing"
                checked={formData.consentDataProcessing}
                onChange={handleChange}
                className={styles.checkbox}
                required
              />
              <label htmlFor="consentDataProcessing">
                {t("careerForm.consentDataProcessing")}{" "}
                <a href="/privacy" target="_blank" rel="noopener noreferrer">
                  {t("careerForm.consentLink")}
                </a>
                .
              </label>
            </div>

            <div className={styles.formGroup}>
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={siteConfig.recaptcha.siteKey}
                onChange={(token) => setCaptchaToken(token)}
                onExpired={() => setCaptchaToken(null)}
              />
            </div>

            <button
              type="submit"
              className={`commonButton buttonLg ${styles.submitButton}`}
              disabled={
                loading ||
                !formData.consent ||
                !formData.consentDataProcessing ||
                !captchaToken
              }
            >
              {loading ? t("careerForm.submitting") : t("careerForm.submit")}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default KariereForm;
